import { useId, useState } from 'react';
import { cn } from 'cn';
import { NoTranslationBadge, SourceBadge, TranslationBadge } from './provenance-badge';
import type { TranslationReading } from '@/features/reader/types';

/**
 * The compact trust affordance: both provenance badges, and one popover that
 * explains them together.
 *
 * It exists because the reader was repeating the same two claims on every
 * passage — twelve labels on a six-passage khutbah — when both are properties
 * of **the translation as a whole, per language**. They belong once, under
 * the title.
 *
 * Three rules it holds:
 *
 * - **The badges never merge.** A verified publisher says nothing about
 *   whether anyone checked the translation, and one combined "trusted" mark
 *   would be the exact conflation brief §5 forbids. The popover says so out
 *   loud rather than leaving it to be inferred.
 * - **Icon and word stay visible.** The explanation moves into the popover;
 *   the claim does not. An icon alone is not a word.
 * - **Hover, focus and tap.** A tooltip that only opens on hover exists on
 *   neither a phone nor a keyboard, and most of these readers are on phones.
 */
export function TrustStrip({
  institutionKey,
  sourceKind,
  institutionName,
  fetchedOn,
  translation,
  stale,
  languageName,
  className,
}: {
  institutionKey: string;
  sourceKind: 'publisher' | 'user';
  institutionName: string | null;
  fetchedOn: string | null;
  translation: TranslationReading | null;
  stale: boolean;
  languageName: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();

  return (
    <div className={cn('relative inline-flex flex-wrap items-center gap-1.5', className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((was) => !was)}
        onMouseEnter={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        className="rounded-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <SourceBadge institutionKey={sourceKind === 'user' ? '' : institutionKey} />
      </button>

      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((was) => !was)}
        onMouseEnter={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        className="rounded-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        {translation === null ? (
          <NoTranslationBadge />
        ) : (
          <TranslationBadge translation={translation} stale={stale} />
        )}
      </button>

      {open ? (
        <div
          id={id}
          role="dialog"
          aria-label="Where this comes from"
          onMouseLeave={() => setOpen(false)}
          className={cn(
            'absolute top-full z-40 mt-2 w-[320px] rounded-lg border border-border',
            'bg-popover p-3 text-popover-foreground shadow-2',
            'start-0 flex flex-col gap-3',
          )}
        >
          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              The Arabic
            </h3>
            <p className="mt-1 text-[13px] leading-[18px]">
              {sourceKind === 'user'
                ? 'Uploaded by a reader, not issued by a ministry.'
                : `Issued by ${institutionName ?? 'the publisher'} and taken from their own publication.`}
              {fetchedOn ? ` Fetched ${fetchedOn}.` : ''}
            </p>
          </section>

          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              The {languageName} translation
            </h3>
            <p className="mt-1 text-[13px] leading-[18px]">
              {translation === null
                ? 'Nobody has translated this khutbah into this language yet.'
                : stale
                  ? 'The publisher replaced the Arabic after this was translated, so the two no longer match.'
                  : translation.origin === 'publisher'
                    ? 'The publisher’s own translation, not ours.'
                    : translation.reviewed
                      ? 'A person read this translation against the Arabic and approved it.'
                      : translation.origin === 'ai'
                        ? 'Produced by a model. Nobody has checked it yet.'
                        : 'Written by a person. Nobody else has checked it yet.'}
            </p>
            {translation !== null && !translation.checked ? (
              <p className="mt-1.5 text-[12px] leading-[17px] text-muted-foreground">
                {/* Said in words, not in the spec's phrase. */}
                An automatic pass is meant to compare the Arabic against the
                translation and flag anything left out, added or changed. It has
                not run.
              </p>
            ) : null}
          </section>

          {/* The sentence the whole component exists for. */}
          <p className="border-t border-border pt-2 text-[12px] leading-[17px] text-muted-foreground">
            These are separate. A trusted source does not make its translation
            checked, and a checked translation does not make its source official.
          </p>
        </div>
      ) : null}
    </div>
  );
}
